import React, { createContext, useCallback, useContext, useState } from 'react'

export interface UploadFileStatus {
  id: string
  fileName: string
  progress: number
  error: any
  result: any
  retry: any
  reset: any
  payload: any
}

interface UploadProgressContextValue {
  files: UploadFileStatus[]
  updateFileStatus: (files: UploadFileStatus) => void
  resetFileStatus: (id: string) => void
}

const UploadProgressContext = createContext<UploadProgressContextValue | null>(null)

const useUploadProgress = () => {
  const context = useContext(UploadProgressContext)
  if (!context) {
    throw new Error('useUploadProgress must be used within an UploadProgressProvider')
  }
  return context
}

const UploadProgressProvider = ({ children }) => {
  const [files, setFiles] = useState<UploadFileStatus[]>([])

  const updateFileStatus = useCallback((file: UploadFileStatus) => {
    if (!file || !file.id) return
    setFiles((prevFiles) => {
      const index = prevFiles.findIndex((f) => f.id === file.id)
      if (index !== -1) {
        return prevFiles.map((f) => (f.id === file.id ? file : f)) // Update existing
      } else {
        return [...prevFiles, file] // Add new
      }
    })
  }, [])

  const resetFileStatus = useCallback((id: string) => {
    setFiles((prevFiles) => prevFiles.filter((f) => f.id !== id))
  }, [])

  return (
    <UploadProgressContext.Provider value={{ files, updateFileStatus, resetFileStatus }}>
      {children}
    </UploadProgressContext.Provider>
  )
}

export { UploadProgressContext, UploadProgressProvider, useUploadProgress }
